import { useMemo, useState } from "react";
import { IGateway } from "@grabcad/company-server-shared/dist/types";

const DEFAULT_ROWS_PER_PAGE = 6;

interface Props {
    gateways: IGateway[];
}

const useGatewayList = ({ gateways }: Props) => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [searchTerm, setSearchTerm] = useState("");
    const [removedGatewayIds, setRemovedGatewayIds] = useState<Set<string>>(new Set());
    const [newThingName, setNewThingName] = useState("");

    const normalizedSearch = searchTerm.trim().toUpperCase();

    const filteredGateways = useMemo(() => {
        const filtered = gateways.filter((gateway) => {
            if (removedGatewayIds.has(gateway.coreDeviceThingName)) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return (gateway.site ?? "").trim().toUpperCase().includes(normalizedSearch);
        });

        if (newThingName) {
            const index = filtered.findIndex(
                (gateway) => gateway.coreDeviceThingName === newThingName
            );

            if (index > 0) {
                const [gateway] = filtered.splice(index, 1);
                filtered.unshift(gateway);
            }
        }

        return filtered;
    }, [gateways, normalizedSearch, removedGatewayIds, newThingName]);

    const firstPageGatewayCount = Math.max(rowsPerPage - 1, 1);

    const totalPages = Math.max(1, Math.ceil((filteredGateways.length + 1) / rowsPerPage));

    // Derive the page instead of synchronizing state in an effect.
    const validPage = Math.min(page, totalPages);

    const visibleGateways = useMemo(() => {
        if (validPage === 1) {
            return filteredGateways.slice(0, firstPageGatewayCount);
        }

        const start = firstPageGatewayCount + (validPage - 2) * rowsPerPage;

        return filteredGateways.slice(start, start + rowsPerPage);
    }, [filteredGateways, validPage, firstPageGatewayCount, rowsPerPage]);

    const visibleRangeStart = filteredGateways.length
        ? validPage === 1
            ? 1
            : firstPageGatewayCount + (validPage - 2) * rowsPerPage + 1
        : 0;

    const visibleRangeEnd = visibleRangeStart ? visibleRangeStart + visibleGateways.length - 1 : 0;

    const removeGateway = (thingName: string): void => {
        setRemovedGatewayIds((previous) => {
            const next = new Set(previous);
            next.add(thingName);
            return next;
        });
    };

    const updateSearchTerm = (value: string): void => {
        setSearchTerm(value);
        setPage(1);
    };

    const updateRowsPerPage = (rows: number): void => {
        if (!Number.isFinite(rows) || rows <= 0) {
            return;
        }

        setRowsPerPage(rows);
        setPage(1);
    };

    return {
        page,
        validPage,
        rowsPerPage,
        totalPages,
        searchTerm,
        filteredGateways,
        visibleGateways,
        visibleRangeStart,
        visibleRangeEnd,
        newThingName,

        setPage,
        setNewThingName,
        updateSearchTerm,
        updateRowsPerPage,
        removeGateway,
    };
};

export default useGatewayList;
